/**
 * useAlertPreferences — React hook over the canonical alert-preference data
 * layer (#231). For non-React usage (lib functions, emit-time callbacks),
 * import from '@/lib/alerts/alertPreferences' directly.
 *
 * Setters delegate to the data layer's read-modify-write and set state from
 * the RETURNED persisted snapshot — never by merging possibly-stale hook
 * state — so same-tab writes stay coherent with storage.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  readAlertPreferences,
  updateAlertPreferences,
  setChannelSettings,
  setChannelVisualEnabled as persistChannelVisualEnabled,
  setChannelDeliveryThreshold as persistChannelDeliveryThreshold,
  subscribeAlertPreferences,
  STORAGE_KEY,
  type AlertPreferences,
} from '@/lib/alerts/alertPreferences';
import type { AlertChannelId } from '@/lib/alerts/registry';

export function useAlertPreferences() {
  const [prefs, setPrefs] = useState<AlertPreferences>(readAlertPreferences);

  // Cross-tab sync: re-read on a sibling tab's write so this hook's snapshot
  // doesn't go stale and clobber the sibling's change on the next update. The
  // `storage` event fires only in OTHER tabs, so no echo loop.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setPrefs(readAlertPreferences());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Same-tab sync: `storage` does NOT fire in the writing tab, so a legacy
  // `useSoundSettings` panel mounted alongside this one would leave us stale.
  useEffect(() => subscribeAlertPreferences(() => setPrefs(readAlertPreferences())), []);

  const setMasterVolume = useCallback((v: number) => {
    setPrefs(updateAlertPreferences({ masterVolume: v }));
  }, []);

  const setMinFillContracts = useCallback((n: number) => {
    setPrefs(updateAlertPreferences({ minFillContracts: n }));
  }, []);

  const setChannelSoundEnabled = useCallback((id: AlertChannelId, enabled: boolean) => {
    setPrefs(setChannelSettings(id, { soundEnabled: enabled }));
  }, []);

  const setChannelVolume = useCallback((id: AlertChannelId, volume: number) => {
    setPrefs(setChannelSettings(id, { volume }));
  }, []);

  /** Independent of sound: muting one channel side never suppresses the other. */
  const setChannelVisualEnabled = useCallback((id: AlertChannelId, enabled: boolean) => {
    setPrefs(persistChannelVisualEnabled(id, enabled));
  }, []);

  /**
   * The persisted value may differ from `threshold` — it is clamped to the
   * channel's bounds and held clear of its ordered sibling. Consumers must
   * render from the returned snapshot, not from what they passed in.
   */
  const setChannelDeliveryThreshold = useCallback((id: AlertChannelId, threshold: number) => {
    setPrefs(persistChannelDeliveryThreshold(id, threshold));
  }, []);

  const setOrderPlacedSoundEnabled = useCallback(
    (enabled: boolean) => setChannelSoundEnabled('orderPlaced', enabled),
    [setChannelSoundEnabled]
  );

  return {
    prefs,
    setMasterVolume,
    setMinFillContracts,
    setChannelSoundEnabled,
    setChannelVolume,
    setChannelVisualEnabled,
    setChannelDeliveryThreshold,
    setOrderPlacedSoundEnabled,
  };
}
